import {
    getCustomer,
    listCustomers
} from './queries';
import {
    createCustomer,
    deleteCustomer,
    purgeCustomer,
    updateCustomer,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {
    Customer,
    CustomerStatus,
    CreateCustomer,
    DeleteCustomer,
    GetCustomer,
    ListCustomers,
    UpdateCustomer
} from "../types.ts";
import { billingApiClient } from "../../billing-api-integration";
import {ListRecordsQuery} from "../../../types";


const client = billingApiClient;


const normalizeFilter = (filter: { [key: string]: string }) =>
    Object.keys(filter).reduce((acc: { [key: string]: string }, key) => {
        if (filter[key] !== '' && filter[key] !== undefined) {
            acc[key] = filter[key];
        }
        return acc;
    }, {});

export const fetchRecords = async (query: ListRecordsQuery) => {
    try {
        const { filter, ...rest } = query;
        const transformedFilter = filter ? normalizeFilter(filter as { [key: string]: string }) : {};

        const response = await client.graphql<GraphQLQuery<ListCustomers>>(
            {
                query: listCustomers,
                variables: {
                    ...rest,
                    ...(Object.keys(transformedFilter).length ? { filter: transformedFilter } : {})
                },
            });

        return {
            items: response.data?.listCustomers.items,
            nextToken: response.data?.listCustomers.nextToken,
        };

    } catch (e: unknown) {
        const err = e as Record<string, unknown>;
        if (typeof err !== 'undefined' && typeof err.errors !== 'undefined') {
            const unauthorizedExceptionFound = (err.errors as Array<{errorType: string, message: string}>).find((item: {
                errorType: string,
                message: string
            }) => item.errorType === 'UnauthorizedException')
            if (unauthorizedExceptionFound) {
                window.location.href = '/'
            }
        }
        throw e;
    }
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetCustomer>>(
        {
            query: getCustomer,
            variables: {id: id},
        });
    return response.data?.getCustomer;
};

export const add = async (payload: Omit<Customer, 'id'> & Record<string, unknown>) => {
    const cleanPayload = {
        ...payload,
        contactInfo: payload.contactInfo ? {
            ...payload.contactInfo,
            phone: payload.contactInfo.phone || null,
            website: payload.contactInfo.website || null
        } : payload.contactInfo
    };

    const response = await client.graphql<GraphQLQuery<CreateCustomer>>(
        {
            query: createCustomer,
            variables: {input: cleanPayload},
        });
    console.debug('in the service response', response);
    return response.data?.createCustomer;
};

export const update = async (id: string, record: Customer) => {

    console.debug('IN UPDATE', id, record);

    // FNP-774: WHITELIST the mutation input to exactly the UpdateCustomerInput
    // fields — never blacklist-and-spread. The fetched Customer carries read-only
    // fields (ownerAccountId/ownerAccountEmail, billingAdmin*, subjectType,
    // organizationId, providerMappings, paymentProfiles — FNP-573/492) that
    // UpdateCustomerInput rejects; spreading `...rest` sent them and broke Save
    // for any customer that had them. Same class as FNP-760/764
    // (feedback_mutation_payload_whitelist_pattern).
    const input: {
        id: string;
        name: string;
        status: CustomerStatus;
        description?: string;
        contactInfo?: { email?: string; phone?: string | null; website?: string | null };
    } = {
        id: record.id,
        name: record.name,
        status: record.status,
    };
    if (record.description != null) {
        input.description = record.description;
    }
    if (record.contactInfo) {
        input.contactInfo = {
            email: record.contactInfo.email,
            phone: record.contactInfo.phone || null,
            website: record.contactInfo.website || null,
        };
    }

    const response = await client.graphql<GraphQLQuery<UpdateCustomer>>(
        {
            query: (updateCustomer),
            variables: {input},
        });
    return response.data?.updateCustomer;
};

export const purgeRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteCustomer>>({
        query: purgeCustomer,
        variables: {input: {id: id}},
    });
    return response.data?.deleteCustomer;
};

export const archiveRecord = async (id: string) => {
    const customer = await fetch(id);
    if (customer) {
        const updatedCustomer = {
            ...customer,
            status: 'ARCHIVED' as const
        };
        return await update(id, updatedCustomer);
    }
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteCustomer>>(
        {
            query: (deleteCustomer),
            variables: {input: {id: id}},

        });
    return response.data?.deleteCustomer;
};
